import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import ReportDTO from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import ApiResponse from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponse";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import GenericCrudService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudService";
import HttpClient from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClient";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";
import Assignation from "../models/Assignation";
import AssignationProjectionReport from "../models/AssignationProjectionReport";
import AssignationTransfer from "../models/AssignationTransfer";
import AssignationTransferBuilder from "../models/AssignationTransferBuilder";
import AssignationTransferForm from "../models/AssignationTransferForm";
import OverStaffAssignation from "../models/OverStaffReport/OverStaffAssignation";
import Position from "../models/Position";
import ProjectData from "../models/ProjectData";
import Projection from "../models/Projection";
import ProjectionAssignation from "../models/ProjectionAssignation";
import ProjectionPeople from "../models/ProjectionPeople";

class AssignationService extends GenericCrudService<Assignation> {
  findByProjectExternalId(
    page: number,
    pageSize: number,
    projectExternalId: string,
    statusFilter: string
  ): Promise<ApiResponse<PagedResult<Assignation>>> {
    let query = `${this.basePath}/find-filtered?page=${page}&pageSize=${pageSize}&projectExternalId=${projectExternalId}&statusFilter=${statusFilter}`;
    return HttpClient.get(query, true);
  }

  parseAssignationTransferForm(formData: AssignationTransferForm): AssignationTransfer {
    const position: Position = {
      externalId: formData.positionExternalId ? formData.positionExternalId : "",
      externalRoleName: formData.externalRoleName,
      internalRoleName: formData.internalRoleName,
      externalHours: formData.externalHours,
      internalHours: formData.internalHours,
      shadow: formData.shadow,
      bonus: formData.bonus,
      billingPolicy: formData.billingPolicy,
      shortEndDate: formData.shortEndDate,
      shortStartDate: formData.shortStartDate,
      roleKey: formData.roleKey,
      onDemand: formData.onDemand,
      monthlyMinimumHours: formData.monthlyMinimumHours,
      memberExternalId: formData.userExternalId,
      projectExternalId: formData.projectExternalId,
    };

    const transfer: AssignationTransfer = {
      externalId: formData.externalId,
      userExternalId: formData.userExternalId,
      oldPositionExternalId: formData.oldPositionExternalId,
      shortStartDate: formData.shortStartDate,
      shortEndDate: formData.shortEndDate,
      position: position,
      userFullName: "",
    };

    return transfer;
  }

  findAssignationsNamesByUser(userExternalId?: string, date: Date | null = null): Promise<ApiResponse<AutoCompleteUIDTO[]>> {
    let query = `${this.basePath}/assignation-names`;
    query += userExternalId ? `?userExternalId=${userExternalId}` : "";
    query +=
      date && userExternalId
        ? `&month=${date.getMonth() + 1}&year=${date.getFullYear()}`
        : date
        ? `?month=${date.getMonth() + 1}&year=${date.getFullYear()}`
        : "";
    return HttpClient.get(query);
  }

  getProjectionData(): Projection {
    let projection = new Projection();
    this.getOverStaffProjection().then((proj) => {
      let response = proj.getContentOrThrowError();

      let peopleArray: ProjectionPeople[] = [];
      response.people.forEach((p) => {
        let assignationArray: ProjectionAssignation[] = [];
        p.assignations.forEach((a) => {
          assignationArray.push(
            new ProjectionAssignation(
              a.projectName,
              a.firstName,
              a.lastName,
              a.userExternalId,
              a.roleType,
              a.externalHours,
              a.bonusHours,
              a.shadow,
              a.externalId
            )
          );
        });
        peopleArray.push(new ProjectionPeople(p.firstName, p.lastName, p.userExternalId, p.email, assignationArray, p.availableHours));
      });

      let projectsArray: ProjectData[] = [];
      response.projects.forEach((p) => {
        let assignationArray: ProjectionAssignation[] = [];
        p.assignations.forEach((a) => {
          assignationArray.push(
            new ProjectionAssignation(
              a.projectName,
              a.firstName,
              a.lastName,
              a.userExternalId,
              a.roleType,
              a.externalHours,
              a.bonusHours,
              a.shadow,
              a.externalId
            )
          );
        });
        projectsArray.push(new ProjectData(p.projectName, assignationArray));
      });

      projection.externalId = response.externalId;
      projection.people = peopleArray;
      projection.projects = projectsArray;
    });
    return projection;
  }

  getAssignationProjection(year: number, month: number, bonused: boolean): Promise<ApiResponse<ReportDTO<AssignationProjectionReport>>> {
    return HttpClient.get(`${this.basePath}/projections?year=${year}&month=${month}&bonused=${bonused}`, true);
  }

  getAssignationDetails(userExternalId: string): Promise<ApiResponse<OverStaffAssignation[]>> {
    return HttpClient.get(`${this.basePath}/details/${userExternalId}`, true);
  }

  getByExternalId(externalId: string): Promise<ApiResponse<Assignation>> {
    return HttpClient.get(`${this.basePath}/${externalId}`, true);
  }

  getOverStaffProjection(): Promise<ApiResponseV2<Projection>> {
    return HttpClientV2.get(`${this.basePath}/over-staff-projection`);
  }

  getAssignationTransferBuilder(
    positionExternalId: string,
    assignationExternalId: string
  ): Promise<ApiResponseV2<AssignationTransferBuilder>> {
    return HttpClientV2.get(`${this.basePath}/switch-assignation-builder/${positionExternalId}/${assignationExternalId}`);
  }

  transferAssignation(assignationForm: AssignationTransferForm): Promise<ApiResponseV2<AssignationTransfer>> {
    const formData = this.parseAssignationTransferForm(assignationForm);
    return HttpClientV2.post(`${this.basePath}/switch-assignation`, formData);
  }

  validateAssignationStartDate(
    positionExternalId: string,
    assignationStartDate: string,
    assignationEndDate: string
  ): Promise<ApiResponse<Boolean>> {
    return HttpClient.get(
      `${this.basePath}/validate-position-date?positionExternalId=${positionExternalId}&` +
        `assignationStartDate=${assignationStartDate}&assignationEndDate=${assignationEndDate}`
    );
  }
}

export default new AssignationService("/ensolvers-ops/assignation");
